import {
  computed,
  Directive,
  type DoCheck,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { BrnFormFieldControl } from '@spartan-ng/brain/form-field';
import { ErrorStateMatcher, ErrorStateTracker } from '@spartan-ng/brain/forms';
import { hlm } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const textareaVariants = cva(
  'hover:bg-components-background-neutral-lighter hover:border-color-stroke-neutral-medium outline-feedback-informative-main border-stroke-neutral-medium placeholder:text-muted-foreground disabled:border-stroke-neutral-medium component-medium-medium disabled:text-components-content-neutral-quaternary bg-components-background-neutral-lightest focus-visible:outline-components-focus focus-visible:bg-components-background-neutral-lighter disabled:bg-color-components-background-neutral-lightest flex [field-sizing:content] min-h-[200px] w-full rounded-md border px-3 py-2 outline-offset-2 transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-100 md:text-sm',
  {
    variants: {
      error: {
        auto: '[&.ng-invalid.ng-touched]:border-feedback-negative-main [&.ng-invalid.ng-touched]:ring-destructive/20',
        true: 'border-feedback-negative-main focus-visible:border-destructive focus-visible:ring-destructive/20 border-2',
      },
    },
    defaultVariants: {
      error: 'auto',
    },
  },
);
type TextareaVariants = VariantProps<typeof textareaVariants>;

@Directive({
  selector: '[hlmTextarea]',
  host: {
    'data-slot': 'textarea',
    '[class]': '_computedClass()',
  },
  providers: [
    {
      provide: BrnFormFieldControl,
      useExisting: forwardRef(() => HlmTextarea),
    },
  ],
})
export class HlmTextarea implements BrnFormFieldControl, DoCheck {
  private readonly _injector = inject(Injector);
  private readonly _additionalClasses = signal<ClassValue>('');

  private readonly _errorStateTracker: ErrorStateTracker;

  private readonly _defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly _parentForm = inject(NgForm, { optional: true });
  private readonly _parentFormGroup = inject(FormGroupDirective, { optional: true });

  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  protected readonly _computedClass = computed(() =>
    hlm(textareaVariants({ error: this._state().error }), this.userClass(), this._additionalClasses()),
  );

  public readonly error = input<TextareaVariants['error']>('auto');

  protected readonly _state = linkedSignal(() => ({ error: this.error() }));

  public readonly ngControl: NgControl | null = this._injector.get(NgControl, null);

  public readonly errorState = computed(() => this._errorStateTracker.errorState());

  constructor() {
    this._errorStateTracker = new ErrorStateTracker(
      this._defaultErrorStateMatcher,
      this.ngControl,
      this._parentFormGroup,
      this._parentForm,
    );

    effect(() => {
      const error = this._errorStateTracker.errorState();
      untracked(() => {
        if (this.ngControl) {
          const shouldShowError = error && this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty);
          this._errorStateTracker.errorState.set(shouldShowError ? true : false);
          this.setError(shouldShowError ? true : 'auto');
        }
      });
    });
  }

  ngDoCheck() {
    this._errorStateTracker.updateErrorState();
  }

  public setError(error: TextareaVariants['error']): void {
    this._state.set({ error });
  }

  public setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }
}
